/** Макс. длительность голосового сообщения (сек). */
export const VOICE_MAX_DURATION_SEC = 300;

/** Мин. длительность, чтобы отправить (сек). */
export const VOICE_MIN_DURATION_SEC = 1;

/** Лимит размера голосового (backend: TELEGRAM_SUPPORT_MAX_VOICE_MB, default 20 MB). */
export const VOICE_MAX_SIZE_BYTES = 20 * 1024 * 1024;

/**
 * MIME для MediaRecorder — приоритет audio/webm, не video/webm.
 * См. SUPPORT_CHAT_VOICE_FRONTEND.md §3.
 */
export const pickVoiceRecorderMime = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'video/webm;codecs=opus',
    'video/webm',
  ];
  return candidates.find((t) => MediaRecorder.isTypeSupported(t));
};

export const formatVoiceDuration = (sec: number): string => {
  const total = Math.max(0, Math.floor(sec));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Файл для поля `voice` (не `attachments`). */
export const createVoiceMessageFile = (blob: Blob, mimeType: string): File => {
  const type = mimeType || blob.type || 'audio/webm';
  const name = type.includes('ogg') ? 'voice.ogg' : 'voice.webm';
  return new File([blob], name, { type });
};

/** Валидация голосового перед отправкой; null — ок. */
export const validateOutgoingVoice = (file: File): string | null => {
  if (file.size > VOICE_MAX_SIZE_BYTES) {
    return `Голосовое слишком большое. Максимум: ${VOICE_MAX_SIZE_BYTES / (1024 * 1024)} МБ`;
  }
  return null;
};

/** Псевдо-волна для fallback, если decode не удался. */
export const generatePlaceholderWaveform = (seed: string, count = 36): number[] => {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return Array.from({ length: count }, (_, i) => {
    const x = Math.sin((h + i * 7) * 0.17) * 0.5 + 0.5;
    return 0.25 + x * 0.75;
  });
};

/** Декодирование аудио в нормализованные амплитуды [0..1]. */
export const extractWaveformFromUrl = async (
  url: string,
  barCount = 36,
): Promise<number[]> => {
  if (typeof AudioContext === 'undefined' && typeof (window as any).webkitAudioContext === 'undefined') {
    return generatePlaceholderWaveform(url, barCount);
  }
  const Ctx = AudioContext || (window as any).webkitAudioContext;
  const ctx = new Ctx();
  try {
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
    const channel = audioBuffer.getChannelData(0);
    if (!channel.length) return generatePlaceholderWaveform(url, barCount);

    const blockSize = Math.max(1, Math.floor(channel.length / barCount));
    const bars: number[] = [];
    for (let i = 0; i < barCount; i += 1) {
      let peak = 0;
      const start = i * blockSize;
      const end = Math.min(start + blockSize, channel.length);
      for (let j = start; j < end; j += 1) {
        peak = Math.max(peak, Math.abs(channel[j]));
      }
      bars.push(peak);
    }
    const max = Math.max(...bars, 0.001);
    return bars.map((b) => 0.2 + (b / max) * 0.8);
  } catch {
    return generatePlaceholderWaveform(url, barCount);
  } finally {
    try {
      await ctx.close();
    } catch {}
  }
};

export const mapAnalyserToBars = (data: Uint8Array, barCount: number): number[] => {
  const step = Math.max(1, Math.floor(data.length / barCount));
  const bars: number[] = [];
  for (let i = 0; i < barCount; i += 1) {
    let sum = 0;
    const start = i * step;
    const end = Math.min(start + step, data.length);
    for (let j = start; j < end; j += 1) {
      sum += data[j];
    }
    const avg = sum / (end - start || 1);
    bars.push(0.15 + (avg / 255) * 0.85);
  }
  return bars;
};
